import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { useLifeCheck } from '@/hooks/useLifeCheck';
import { addDays, differenceInDays, parseISO, startOfDay, endOfMonth, startOfMonth, startOfWeek } from 'date-fns';
import { buildFamilyCustodyScopeFilter, mapFamilyCustodyShareRow } from '@/lib/family-custody';
import { getTopLifeCheckPriority, isLifeCheckStale } from '@/lib/life-check';
import { buildBudgetCategoryCollections } from '../../ui-foundation/features/money';
import { buildTopPetHomeAttention } from '../../ui-foundation/features/pets';
import { AppNudgeArrow, AppNudgeButton, AppNudgeText } from '../../ui-foundation';
import { buildQuickReminderSectionNudge } from '../../ui-foundation/features/notifications';

type Section = 'money' | 'home' | 'health' | 'family' | 'work';

interface NudgeData {
  icon: string;
  text: string;
  linkPath?: string;
}

function getRemortgageNudge(fixedRateEnd: string | null) {
  if (!fixedRateEnd) return null;
  const daysUntil = differenceInDays(parseISO(fixedRateEnd), startOfDay(new Date()));
  if (daysUntil > 180) return null;
  if (daysUntil <= 30) {
    return daysUntil < 0
      ? 'Your fixed rate has already ended — review your remortgage options now'
      : `Your fixed rate ends in ${daysUntil} day${daysUntil === 1 ? '' : 's'} — move on remortgage prep now`;
  }
  if (daysUntil <= 90) {
    return `Your fixed rate ends in ${daysUntil} day${daysUntil === 1 ? '' : 's'} — gather remortgage paperwork now`;
  }
  return `Your fixed rate ends in ${daysUntil} day${daysUntil === 1 ? '' : 's'} — start comparing remortgage options`;
}

function useSectionNudge(section: Section): NudgeData | null {
  const { data, user } = useApp();
  const { latestLifeCheck, loading: lifeCheckLoading } = useLifeCheck();
  const [nudge, setNudge] = useState<NudgeData | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const handleRefresh = () => setRefreshTick((current) => current + 1);
    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, []);

  useEffect(() => {
    if (!user || lifeCheckLoading) return;

    const compute = async () => {
      const latestPriority = getTopLifeCheckPriority(latestLifeCheck?.summary);
      if (latestPriority && !isLifeCheckStale(latestLifeCheck) && latestPriority.section === section) {
        setNudge({
          icon: latestPriority.emoji,
          text: `Life Check says ${latestPriority.label} needs attention next — start here before the week gets noisier`,
          linkPath: latestPriority.path,
        });
        return;
      }

      const quickReminderNudge = buildQuickReminderSectionNudge(data.events);
      if (quickReminderNudge) {
        setNudge({
          icon: '⏰',
          text: quickReminderNudge,
          linkPath: '/calendar',
        });
        return;
      }

      switch (section) {
        case 'money': {
          const currentMonth = new Date().toISOString().slice(0, 7);
          const visibleBudgetCategories = buildBudgetCategoryCollections({
            budgetLimitCategories: data.budgetLimits.map((limit) => limit.category),
            expenseCategories: data.expenses.map((expense) => expense.category),
            preferences: data.budgetCategoryPreferences,
          }).visible;
          const overspentCategory = data.budgetLimits
            .filter((limit) => visibleBudgetCategories.includes(limit.category))
            .map((limit) => ({
              category: limit.category,
              limit: limit.limit,
              spent: data.expenses
                .filter((expense) => expense.date.startsWith(currentMonth) && expense.category === limit.category)
                .reduce((sum, expense) => sum + expense.amount, 0),
            }))
            .find((entry) => entry.limit > 0 && entry.spent > entry.limit);

          if (overspentCategory) {
            setNudge({
              icon: '⚠️',
              text: `${overspentCategory.category} is over budget — open Budget to rebalance this month`,
              linkPath: '/money?tab=budget',
            });
            break;
          }

          const { count } = await supabase
            .from('benefits_viewed')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);
          if (!count || count === 0) {
            setNudge({
              icon: '✨',
              text: 'You may be missing benefits — run your scan in 2 minutes',
              linkPath: '/money?tab=benefits',
            });
          } else {
            setNudge(null);
          }
          break;
        }
        case 'home': {
          const [{ data: homeProfile }, { data: maintenanceItems }, { data: pets }, { data: petReminders }, petFoodProfilesResult] = await Promise.all([
            supabase
              .from('home_profiles')
              .select(`
                id,
                address,
                tenure,
                mortgage_provider,
                mortgage_fixed_rate_end,
                epc_rating,
                ground_rent_amount,
                service_charge_amount,
                utility_gas_provider,
                utility_gas_contract_end,
                utility_electric_provider,
                utility_electric_contract_end,
                utility_water_provider,
                utility_broadband_provider,
                utility_broadband_contract_end,
                utility_phone_provider,
                utility_phone_contract_end
              `)
              .eq('user_id', user.id)
              .limit(1)
              .maybeSingle(),
            supabase
              .from('home_maintenance_items' as any)
              .select('id, title, due_date, status')
              .eq('user_id', user.id)
              .neq('status', 'done')
              .order('due_date', { ascending: true, nullsFirst: false }),
            supabase
              .from('pets')
              .select('id, name, pet_type')
              .order('name', { ascending: true }),
            supabase
              .from('health_reminders')
              .select('id, title, due_date, subject_type, subject_id')
              .eq('user_id', user.id)
              .eq('subject_type', 'pet')
              .is('completed_at', null)
              .order('due_date', { ascending: true, nullsFirst: false }),
            (supabase as any)
              .from('pet_food_profiles')
              .select('id, pet_id, brand_name, flavour, package_size, price, last_purchase_date, lasts_for_days, refill_reminder_days_before, notes')
              .order('created_at', { ascending: false }),
          ]);

          if (!homeProfile?.id || !homeProfile.address) {
            setNudge({
              icon: '🏠',
              text: 'Add your home profile so everything else in Home can line up properly',
              linkPath: '/home?tab=my-home',
            });
            break;
          }

          const hasOwnerDetails = homeProfile.tenure === 'owner-occupier' && Boolean(
            homeProfile.mortgage_provider
            || homeProfile.mortgage_fixed_rate_end
            || homeProfile.epc_rating
            || homeProfile.ground_rent_amount != null
            || homeProfile.service_charge_amount != null
          );

          if (homeProfile.tenure === 'owner-occupier' && !hasOwnerDetails) {
            setNudge({
              icon: '🔑',
              text: 'Add mortgage, EPC, or service-charge details so owner admin has a proper home',
              linkPath: '/home?tab=my-home',
            });
            break;
          }

          if (homeProfile.tenure === 'owner-occupier' && homeProfile.mortgage_provider && !homeProfile.mortgage_fixed_rate_end) {
            setNudge({
              icon: '📅',
              text: 'Add your fixed-rate end date before remortgage timing sneaks up on you',
              linkPath: '/home?tab=my-home',
            });
            break;
          }

          if (homeProfile.tenure === 'owner-occupier' && homeProfile.mortgage_fixed_rate_end) {
            const remortgageNudge = getRemortgageNudge(homeProfile.mortgage_fixed_rate_end);
            if (remortgageNudge) {
              setNudge({
                icon: '🏦',
                text: remortgageNudge,
                linkPath: '/home?tab=my-home',
              });
              break;
            }
          }

          const petHomeAttention = buildTopPetHomeAttention({
            pets: ((pets || []) as any[]).map((pet) => ({
              id: pet.id,
              name: pet.name,
              petType: pet.pet_type,
            })),
            healthReminders: ((petReminders || []) as any[]).map((reminder) => ({
              id: reminder.id,
              title: reminder.title,
              dueDate: reminder.due_date,
              subjectType: reminder.subject_type,
              subjectId: reminder.subject_id,
            })),
            petFoodProfiles: (((petFoodProfilesResult as any).data || []) as any[]).map((profile) => ({
              id: profile.id,
              pet_id: profile.pet_id,
              brand_name: profile.brand_name,
              flavour: profile.flavour,
              package_size: profile.package_size,
              price: profile.price,
              last_purchase_date: profile.last_purchase_date,
              lasts_for_days: profile.lasts_for_days,
              refill_reminder_days_before: profile.refill_reminder_days_before,
              notes: profile.notes,
            })),
          });

          if (petHomeAttention) {
            setNudge({
              icon: petHomeAttention.emoji,
              text: petHomeAttention.text,
              linkPath: petHomeAttention.path,
            });
            break;
          }

          const hasUtilityProviders = Boolean(
            homeProfile.utility_gas_provider
            || homeProfile.utility_electric_provider
            || homeProfile.utility_water_provider
            || homeProfile.utility_broadband_provider
            || homeProfile.utility_phone_provider
          );
          const hasRenewalUtilityProviders = Boolean(
            homeProfile.utility_gas_provider
            || homeProfile.utility_electric_provider
            || homeProfile.utility_broadband_provider
            || homeProfile.utility_phone_provider
          );

          if (!hasUtilityProviders) {
            setNudge({
              icon: '🔌',
              text: 'Add your utility providers so switching and renewal reminders have a proper home',
              linkPath: '/home?tab=my-home',
            });
            break;
          }

          const utilityContracts = [
            {
              label: 'Gas',
              provider: homeProfile.utility_gas_provider,
              contractEnd: homeProfile.utility_gas_contract_end,
            },
            {
              label: 'Electric',
              provider: homeProfile.utility_electric_provider,
              contractEnd: homeProfile.utility_electric_contract_end,
            },
            {
              label: 'Broadband',
              provider: homeProfile.utility_broadband_provider,
              contractEnd: homeProfile.utility_broadband_contract_end,
            },
            {
              label: 'Phone',
              provider: homeProfile.utility_phone_provider,
              contractEnd: homeProfile.utility_phone_contract_end,
            },
          ];
          const contractDatesMissing = utilityContracts.filter((item) => item.provider && !item.contractEnd);
          if (contractDatesMissing.length > 0) {
            setNudge({
              icon: '📅',
              text: `Add contract end dates for ${contractDatesMissing.map((item) => item.label.toLowerCase()).join(', ')} so Adulting can flag when to compare prices`,
              linkPath: '/home?tab=my-home',
            });
            break;
          }

          const nextComparisonWindow = utilityContracts
            .filter((item) => item.provider && item.contractEnd)
            .map((item) => ({
              ...item,
              daysUntil: differenceInDays(parseISO(item.contractEnd as string), startOfDay(new Date())),
            }))
            .filter((item) => item.daysUntil >= 0 && item.daysUntil <= 45)
            .sort((a, b) => a.daysUntil - b.daysUntil)[0];

          if (nextComparisonWindow) {
            setNudge({
              icon: '💷',
              text: nextComparisonWindow.daysUntil === 0
                ? `${nextComparisonWindow.label} contract ends today — compare prices before it rolls over`
                : `${nextComparisonWindow.label} contract ends in ${nextComparisonWindow.daysUntil} day${nextComparisonWindow.daysUntil === 1 ? '' : 's'} — compare prices now`,
              linkPath: '/home?tab=my-home&section=bills',
            });
            break;
          }

          const urgentHomeItem = (maintenanceItems || []).find((item: any) => {
            if (!item.due_date) return false;
            const days = differenceInDays(parseISO(item.due_date), startOfDay(new Date()));
            return days <= 14;
          });

          if (urgentHomeItem) {
            const days = differenceInDays(parseISO(urgentHomeItem.due_date), startOfDay(new Date()));
            setNudge({
              icon: '🧰',
              text: days < 0
                ? `${urgentHomeItem.title} is overdue`
                : `${urgentHomeItem.title} needs attention in ${days} day${days === 1 ? '' : 's'}`,
              linkPath: '/home?tab=admin',
            });
            break;
          }

          if (!maintenanceItems || maintenanceItems.length === 0) {
            setNudge({
              icon: '🧰',
              text: 'Start a home admin item so repairs and moving jobs stop living only in your head',
              linkPath: '/home?tab=admin',
            });
            break;
          }

          const { data: renewals } = await supabase
            .from('renewals')
            .select('*')
            .eq('user_id', user.id);
          const utilityRenewals = (renewals || []).filter((renewal) =>
            renewal.type === 'energy' || renewal.type === 'broadband' || renewal.type === 'phone'
          );

          if (hasRenewalUtilityProviders && utilityRenewals.length === 0) {
            setNudge({
              icon: '💡',
              text: 'You have energy, broadband, or phone providers logged — add their renewal dates before a rollover catches you',
              linkPath: '/home?tab=my-home&section=bills',
            });
            break;
          }

          if (!renewals || renewals.length === 0) {
            setNudge({
              icon: '🔔',
              text: 'Track your renewals here — never get auto-rolled over again',
              linkPath: '/home?tab=my-home&section=bills',
            });
          } else {
            // Find soonest renewal within 30 days
            const today = startOfDay(new Date());
            const upcoming = renewals
              .map(r => ({ ...r, daysUntil: differenceInDays(parseISO(r.renewal_date), today) }))
              .filter(r => r.daysUntil >= 0 && r.daysUntil <= 30)
              .sort((a, b) => a.daysUntil - b.daysUntil);
            if (upcoming.length > 0) {
              const r = upcoming[0];
              setNudge({
                icon: '⚠️',
                text: `${r.provider} renews in ${r.daysUntil} day${r.daysUntil === 1 ? '' : 's'}`,
                linkPath: '/home?tab=my-home&section=bills',
              });
            } else {
              setNudge(null);
            }
          }
          break;
        }
        case 'health': {
          const missingHealthProfile =
            !data.profile.dateOfBirth
            || !data.profile.screeningInvitationPath
            || data.profile.hasCervix === null
            || data.profile.breastScreeningRelevant === null
            || data.profile.prostateScreeningRelevant === null;

          if (missingHealthProfile) {
            setNudge({
              icon: '🩺',
              text: 'Finish your health profile so Adulting stops guessing what applies to you',
              linkPath: '/profile',
            });
            break;
          }

          const todayStr = startOfDay(new Date()).toISOString();
          const { count } = await supabase
            .from('wellbeing_checkins')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .gte('checked_in_at', todayStr);
          if (!count || count === 0) {
            setNudge({
              icon: '✨',
              text: 'How are you doing today? Daily check-in takes 10 seconds',
              linkPath: '/health?tab=wellbeing',
            });
          } else {
            setNudge(null);
          }
          break;
        }
        case 'family': {
          const hasChildren = data.profile.children.some((child) => child.name.trim().length > 0);
          const hasAdultCareContext = data.profile.dependantTypes.includes('elderly') || data.profile.dependantTypes.includes('disabled');
          const now = new Date();
          const monthStartStr = startOfMonth(now).toISOString().split('T')[0];
          const monthEndStr = endOfMonth(now).toISOString().split('T')[0];
          const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
          const currentWeekStartStr = currentWeekStart.toISOString().split('T')[0];
          const currentWeekEndStr = addDays(currentWeekStart, 6).toISOString().split('T')[0];
          const { data: custodyShareRow } = await supabase
            .from('family_custody_shares' as any)
            .select('*')
            .or(`owner_user_id.eq.${user.id},member_user_id.eq.${user.id}`)
            .maybeSingle();
          const custodyShare = mapFamilyCustodyShareRow(custodyShareRow);
          const custodyScope = buildFamilyCustodyScopeFilter(user.id, custodyShare?.id || null);
          const [{ data: events }, { data: adminItems }, { data: childProfiles }, { count: adultDependantCount }, { data: childcareRecords }, { data: custodyEvents }, { data: custodyRequests }, { data: careReviews }, { count: mealPlanCount }, { count: mealCount }] = await Promise.all([
            supabase
              .from('events')
              .select('id')
              .eq('user_id', user.id)
              .gte('date', monthStartStr)
              .lte('date', monthEndStr),
            supabase
              .from('family_admin_items' as any)
              .select('id, title, due_date, status, reminder_days_before, area')
              .eq('user_id', user.id)
              .neq('status', 'done')
              .order('due_date', { ascending: true, nullsFirst: false }),
            supabase
              .from('children')
              .select('id, name, date_of_birth, nhs_number, gp_surgery, school_name, emergency_contacts, allergies, medications, notes')
              .eq('user_id', user.id),
            supabase
              .from('adult_dependants')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', user.id),
            supabase
              .from('family_childcare_records')
              .select('id, child_name, contract_end_date, tax_free_top_up_date, voucher_renewal_date')
              .eq('user_id', user.id),
            supabase
              .from('family_custody_events' as any)
              .select('id, child_name, handover_date, status')
              .or(custodyScope)
              .order('handover_date', { ascending: true }),
            supabase
              .from('family_custody_requests' as any)
              .select('id, child_name, requested_for_date, status')
              .or(custodyScope)
              .order('requested_for_date', { ascending: true }),
            supabase
              .from('family_care_reviews' as any)
              .select('id, dependant_name, due_date, reminder_days_before, status')
              .eq('user_id', user.id)
              .order('due_date', { ascending: true }),
            supabase
              .from('family_meal_plans' as any)
              .select('id', { count: 'exact', head: true })
              .eq('user_id', user.id)
              .eq('week_start_date', currentWeekStartStr),
            supabase
              .from('family_meal_plan_meals' as any)
              .select('id', { count: 'exact', head: true })
              .eq('user_id', user.id)
              .gte('meal_date', currentWeekStartStr)
              .lte('meal_date', currentWeekEndStr),
          ]);
          const hasStructuredChildProfiles = (childProfiles || []).some((child) =>
            child.name?.trim().length > 0 && Boolean(
              child.date_of_birth
              || child.nhs_number
              || child.gp_surgery
              || child.school_name
              || child.emergency_contacts
              || child.allergies
              || child.medications
              || child.notes
            )
          );

          const childcareAttention = ((childcareRecords || []) as any[])
            .flatMap((record) => ([
              record.contract_end_date ? { childName: record.child_name, label: 'childcare contract', date: record.contract_end_date } : null,
              record.tax_free_top_up_date ? { childName: record.child_name, label: 'Tax-Free Childcare top-up', date: record.tax_free_top_up_date } : null,
              record.voucher_renewal_date ? { childName: record.child_name, label: 'childcare voucher renewal', date: record.voucher_renewal_date } : null,
            ]))
            .filter(Boolean)
            .map((item: any) => ({
              ...item,
              daysUntil: differenceInDays(parseISO(item.date), startOfDay(new Date())),
            }))
            .sort((left: any, right: any) => left.daysUntil - right.daysUntil)[0];

          if (childcareAttention && childcareAttention.daysUntil <= 30) {
            setNudge({
              icon: '🧒',
              text: childcareAttention.daysUntil < 0
                ? `${childcareAttention.childName || 'Childcare'} ${childcareAttention.label} is overdue`
                : `${childcareAttention.childName || 'Childcare'} ${childcareAttention.label} is due in ${childcareAttention.daysUntil} day${childcareAttention.daysUntil === 1 ? '' : 's'}`,
              linkPath: '/family?tab=admin',
            });
            break;
          }

          const urgentAdminItem = (adminItems || []).find((item: any) => {
            if (!item.due_date) return false;
            return differenceInDays(parseISO(item.due_date), startOfDay(new Date())) <= (item.reminder_days_before ?? 14);
          });

          const upcomingCustody = ((custodyEvents || []) as any[])
            .map((item) => ({
              childName: item.child_name,
              handoverDate: item.handover_date,
              daysUntil: differenceInDays(parseISO(item.handover_date), startOfDay(new Date())),
              status: item.status,
            }))
            .filter((item) => item.status !== 'done')
            .sort((left, right) => left.daysUntil - right.daysUntil)[0];

          const pendingCustodyRequest = ((custodyRequests || []) as any[])
            .find((item) => item.status === 'proposed');

          const hasCoParentingContext = (adminItems || []).some((item: any) => item.area === 'co-parenting')
            || (custodyEvents?.length || 0) > 0
            || (custodyRequests?.length || 0) > 0;
          const upcomingCareReview = ((careReviews || []) as any[])
            .map((item) => ({
              dependantName: item.dependant_name,
              dueDate: item.due_date,
              reminderDaysBefore: item.reminder_days_before ?? 21,
              daysUntil: differenceInDays(parseISO(item.due_date), startOfDay(new Date())),
              status: item.status,
            }))
            .filter((item) => item.status !== 'done')
            .sort((left, right) => left.daysUntil - right.daysUntil)[0];

          if (urgentAdminItem) {
            const days = differenceInDays(parseISO(urgentAdminItem.due_date), startOfDay(new Date()));
            setNudge({
              icon: '🧾',
              text: days < 0
                ? `${urgentAdminItem.title} is overdue`
                : `${urgentAdminItem.title} is due in ${days} day${days === 1 ? '' : 's'}`,
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if (upcomingCustody && upcomingCustody.daysUntil <= 14) {
            setNudge({
              icon: '🤝',
              text: upcomingCustody.daysUntil < 0
                ? `${upcomingCustody.childName || 'Shared custody'} handover date has passed — update the Family calendar trail`
                : `${upcomingCustody.childName || 'Shared custody'} handover is in ${upcomingCustody.daysUntil} day${upcomingCustody.daysUntil === 1 ? '' : 's'}`,
              linkPath: '/family',
            });
            break;
          }

          if (pendingCustodyRequest) {
            const requestDays = differenceInDays(parseISO(pendingCustodyRequest.requested_for_date), startOfDay(new Date()));
            setNudge({
              icon: '📝',
              text: requestDays < 0
                ? `${pendingCustodyRequest.child_name || 'Co-parenting'} request still needs closing out`
                : `${pendingCustodyRequest.child_name || 'Co-parenting'} request is live for ${requestDays === 0 ? 'today' : `${requestDays} day${requestDays === 1 ? '' : 's'} from now`}`,
              linkPath: '/family',
            });
            break;
          }

          if (hasCoParentingContext && (!custodyEvents || custodyEvents.length === 0)) {
            setNudge({
              icon: '🤝',
              text: 'Add the next handover date so co-parenting changes live in the Family calendar too',
              linkPath: '/family',
            });
            break;
          }

          if (upcomingCareReview && upcomingCareReview.daysUntil <= upcomingCareReview.reminderDaysBefore) {
            setNudge({
              icon: '🫶',
              text: upcomingCareReview.daysUntil < 0
                ? `${upcomingCareReview.dependantName || 'Care review'} is overdue`
                : `${upcomingCareReview.dependantName || 'Care review'} is due in ${upcomingCareReview.daysUntil} day${upcomingCareReview.daysUntil === 1 ? '' : 's'}`,
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if ((adultDependantCount || 0) > 0 && (!careReviews || careReviews.length === 0)) {
            setNudge({
              icon: '🫶',
              text: 'Add the next care review or carers assessment so caring admin is visible before it turns urgent',
              linkPath: '/family?tab=admin',
            });
            break;
          }

          const hasMealPlanningContext = Boolean(
            data.profile.partnerName.trim()
            || hasChildren
            || (mealPlanCount || 0) > 0
            || (mealCount || 0) > 0
          );

          if (hasMealPlanningContext && (!mealPlanCount || mealPlanCount === 0)) {
            setNudge({
              icon: '🍽️',
              text: 'Start this week’s meal plan so dinner and the shopping list stop living in your head',
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if ((mealPlanCount || 0) > 0 && (!mealCount || mealCount === 0)) {
            setNudge({
              icon: '🛒',
              text: 'Your weekly meal plan is open — add the first meal so the shopping list can build itself',
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if (hasAdultCareContext && (!adultDependantCount || adultDependantCount === 0)) {
            setNudge({
              icon: '🫶',
              text: 'Add the adult dependant details you would need in a crisis',
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if (hasChildren && !hasStructuredChildProfiles) {
            setNudge({
              icon: '👨‍👩‍👧',
              text: 'Add one child profile so school, GP, and emergency details are not scattered',
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if ((adminItems || []).length === 0 && hasChildren) {
            setNudge({
              icon: '🧾',
              text: 'Set up one family admin item so school, childcare, or care tasks have a proper home',
              linkPath: '/family?tab=admin',
            });
            break;
          }

          if (hasChildren && (!events || events.length === 0)) {
            setNudge({
              icon: '👨‍👩‍👧',
              text: 'No family dates in your diary yet — add one before something gets missed',
              linkPath: '/family',
            });
          } else if (!events || events.length === 0) {
            setNudge(null);
          } else {
            setNudge({
              icon: '🔔',
              text: `You have ${events.length} event${events.length === 1 ? '' : 's'} coming up this month`,
            });
          }
          break;
        }
        case 'work': {
          const selfEmploymentActive = ['self-employed', 'mix'].includes(data.profile.workType);
          const today = startOfDay(new Date()).toISOString().split('T')[0];
          const [{ data: deadlines }, { data: invoices }] = await Promise.all([
            supabase
              .from('hmrc_deadlines')
              .select('*')
              .eq('user_id', user.id)
              .eq('completed', false)
              .gte('deadline_date', today)
              .order('deadline_date')
              .limit(1),
            supabase
              .from('work_invoices' as any)
              .select('id, client_name, due_date, status')
              .eq('user_id', user.id)
              .neq('status', 'paid')
              .order('due_date', { ascending: true }),
          ]);

          const overdueInvoice = (invoices || []).find((invoice: any) =>
            differenceInDays(parseISO(invoice.due_date), startOfDay(new Date())) < 0
          );

          if (overdueInvoice) {
            setNudge({
              icon: '🧾',
              text: `${overdueInvoice.client_name} is overdue — update the invoice status now`,
              linkPath: '/work?tab=self-employed',
            });
            break;
          }

          if (selfEmploymentActive && (!invoices || invoices.length === 0)) {
            setNudge({
              icon: '🧾',
              text: 'Start your invoice tracker so money owed to you is visible alongside HMRC admin',
              linkPath: '/work?tab=self-employed',
            });
            break;
          }

          if (selfEmploymentActive && (!deadlines || deadlines.length === 0)) {
            setNudge({
              icon: '💼',
              text: 'Set up HMRC deadlines now so work admin stops sneaking up on you',
              linkPath: '/work?tab=self-employed',
            });
          } else if (deadlines && deadlines.length > 0) {
            const d = deadlines[0];
            const days = differenceInDays(parseISO(d.deadline_date), startOfDay(new Date()));
            setNudge({
              icon: '⚠️',
              text: `${d.title} due in ${days} day${days === 1 ? '' : 's'}`,
              linkPath: '/work?tab=self-employed',
            });
          } else {
            setNudge(null);
          }
          break;
        }
      }
    };
    compute();
  }, [section, user, data, refreshTick, latestLifeCheck, lifeCheckLoading]);

  return nudge;
}

export default function SectionNudge({ section }: { section: Section }) {
  const nudge = useSectionNudge(section);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  void nudge;
  void navigate;
  void dismissed;
  void setDismissed;
  return null;
}
